import React, { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { RootState } from '../../../../store/root-reducer';
import {
  LeadFinderPrimaryTab,
  LeadFinderSecondaryTab,
  SavedTabFilters,
} from '../../types';
import {
  setActiveLeadType,
  setActiveSubTab,
  clearPeopleSelection,
  clearCompanySelection,
  setBulkPeopleSearch,
  setBulkPeopleModeActive,
} from '../../v2-lead-slice';

interface UseTabNavigationProps {
  setLeadsData: (data: any) => void;
  setCompaniesData: (data: any) => void;
  handleFetchCounts: (payload?: any) => void;
  handleCompanyFetchCounts: (payload?: any) => void;
  searchLeads: (payload?: any) => void;
  searchCompanies: (payload?: any) => void;
  handleRouteChange?: () => void;
  setSavedFilters?: React.Dispatch<React.SetStateAction<SavedTabFilters>>;
  handleDefaultValues?: (reset?: boolean) => SavedTabFilters;
  refreshPeopleLists?: () => void;
  refreshCompanyLists?: () => void;
  skipNextCountCallRef?: React.MutableRefObject<boolean>;
  skipUrlPushForBulkSearchRefProp?: React.MutableRefObject<boolean>;
  tabSpecificBulkModeRef?: React.MutableRefObject<{
    people: boolean;
    company: boolean;
  }>;
}

export const useTabNavigation = (props: UseTabNavigationProps) => {
  const {
    setLeadsData,
    setCompaniesData,
    handleFetchCounts,
    handleCompanyFetchCounts,
    searchLeads,
    searchCompanies,
    handleRouteChange,
    setSavedFilters,
    handleDefaultValues,
    refreshPeopleLists,
    refreshCompanyLists,
    skipNextCountCallRef,
    skipUrlPushForBulkSearchRefProp,
    tabSpecificBulkModeRef,
  } = props;

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const { activeLeadType, activeSubTab, bulkPeopleSearch } = useSelector(
    (state: RootState) => state.leadFinderV2,
  );

  // Own refs
  const preservedFiltersRef = useRef<{
    saved: string;
    totalNew: string;
  }>({ saved: '', totalNew: '' });

  const tabSpecificFiltersRef = useRef<{
    people: string;
    company: string;
  }>({ people: '', company: '' });

  const skipUrlPushForBulkSearchOwnRef = useRef(false);
  // Use provided ref if available (allows sharing with useLeadFinder/useCompanyFinder)
  const skipUrlPushForBulkSearchRef =
    skipUrlPushForBulkSearchRefProp ?? skipUrlPushForBulkSearchOwnRef;

  /**
   * Returns all URL params except pagination (page, start, take).
   * Used when storing/restoring filters per tab on tab switch.
   */
  const getAllFiltersExceptPagination = useCallback(
    (params: URLSearchParams): URLSearchParams => {
      const result = new URLSearchParams();
      const paginationKeys = new Set(['page', 'start', 'take']);
      params.forEach((value, key) => {
        if (!paginationKeys.has(key)) {
          result.append(key, value);
        }
      });
      return result;
    },
    [],
  );

  /** Clear all: clear table; call count API only when in bulk search (URL already '', so finder effect won't run) */
  const handleClearAllPeople = useCallback(() => {
    setLeadsData({ profiles: [], pagination: null });
    if (
      skipUrlPushForBulkSearchRef.current &&
      activeSubTab !== LeadFinderSecondaryTab.SAVED
    ) {
      handleFetchCounts({});
      tabSpecificFiltersRef.current.people = '';
      dispatch(setBulkPeopleModeActive(false));
    }
  }, [setLeadsData, activeSubTab, handleFetchCounts, dispatch]);

  /** Clear all: clear table; call count API only when in bulk search (URL already '', so finder effect won't run) */
  const handleClearAllCompany = useCallback(() => {
    setCompaniesData({ profiles: [], pagination: null });
    tabSpecificFiltersRef.current.company = '';
    if (tabSpecificBulkModeRef) {
      tabSpecificBulkModeRef.current.company = false;
    }
    if (
      skipUrlPushForBulkSearchRef.current &&
      activeSubTab !== LeadFinderSecondaryTab.SAVED
    ) {
      handleCompanyFetchCounts({});
    }
  }, [setCompaniesData, activeSubTab, handleCompanyFetchCounts]);

  // Handle lead type tab change (people/company)
  const handleLeadTypeChange = useCallback(
    (newLeadType: LeadFinderPrimaryTab) => {
      if (newLeadType === activeLeadType) {
        return;
      }

      const currentParams = new URLSearchParams(location.search);

      // Step 1: Store current tab's filters (all except pagination) before switching away
      const currentTabFilters = getAllFiltersExceptPagination(currentParams);
      const currentPath = location.pathname || '';
      const currentTabKey = currentPath.includes('/v2/leads/company/')
        ? 'company'
        : 'people';
      const isLeavingPeopleInBulkMode =
        currentTabKey === 'people' &&
        (skipUrlPushForBulkSearchRef.current ||
          bulkPeopleSearch.isBulkModeActive);
      const isLeavingCompanyInBulkMode =
        currentTabKey === 'company' &&
        !!tabSpecificBulkModeRef?.current?.company;

      if (isLeavingPeopleInBulkMode) {
        tabSpecificFiltersRef.current.people =
          tabSpecificFiltersRef.current.people ||
          bulkPeopleSearch.filters ||
          '';
      } else if (isLeavingCompanyInBulkMode) {
        tabSpecificFiltersRef.current.company =
          tabSpecificFiltersRef.current.company || '';
      } else {
        tabSpecificFiltersRef.current[
          currentTabKey
        ] = currentTabFilters.toString();
      }

      // Step 2: Get stored filters for the target tab
      const targetTabKey =
        newLeadType === LeadFinderPrimaryTab.PEOPLE ? 'people' : 'company';
      if (
        targetTabKey === 'company' &&
        !tabSpecificFiltersRef.current.company &&
        (location as any)?.state?.preservedCompanyFilters
      ) {
        tabSpecificFiltersRef.current.company = String(
          (location as any).state.preservedCompanyFilters,
        );
      }
      let storedParams = new URLSearchParams();
      if (
        newLeadType === LeadFinderPrimaryTab.PEOPLE &&
        bulkPeopleSearch.isBulkModeActive &&
        tabSpecificFiltersRef.current.people
      ) {
        dispatch(
          setBulkPeopleSearch({
            filters: tabSpecificFiltersRef.current.people,
          }),
        );
        dispatch(setBulkPeopleModeActive(true));
      } else if (
        targetTabKey === 'company' &&
        tabSpecificBulkModeRef?.current?.company &&
        tabSpecificFiltersRef.current.company
      ) {
        storedParams = new URLSearchParams();
      } else if (tabSpecificFiltersRef.current[targetTabKey]) {
        storedParams = new URLSearchParams(
          tabSpecificFiltersRef.current[targetTabKey],
        );
      }

      const shouldKeepUrlCleanForTargetTab =
        (targetTabKey === 'company' &&
          tabSpecificBulkModeRef?.current?.company) ||
        (targetTabKey === 'people' && tabSpecificBulkModeRef?.current?.people);

      // Step 3: Preserve pagination params from current URL if desired
      const paginationKeys = ['page', 'start', 'take'];
      paginationKeys.forEach((key) => {
        const value = currentParams.get(key);
        if (value) {
          storedParams.set(key, value);
        }
      });

      // Step 4: Clear selection state for the tab we're leaving
      if (newLeadType === LeadFinderPrimaryTab.COMPANY) {
        dispatch(clearPeopleSelection());
      } else {
        dispatch(clearCompanySelection());
      }

      // Step 5: Update state and navigate
      dispatch(setActiveLeadType(newLeadType));
      setLeadsData({
        profiles: [],
        pagination: null,
      });
      if (handleRouteChange) {
        handleRouteChange();
      }
      if (setSavedFilters && handleDefaultValues) {
        setSavedFilters(handleDefaultValues(true));
      }
      preservedFiltersRef.current = { saved: '', totalNew: '' };

      const newSearch = shouldKeepUrlCleanForTargetTab
        ? ''
        : storedParams.toString();
      history.push(
        `/v2/leads/${newLeadType}/${activeSubTab}${
          newSearch ? `?${newSearch}` : ''
        }`,
      );

      if (newLeadType === LeadFinderPrimaryTab.PEOPLE) {
        if (refreshCompanyLists) refreshCompanyLists();
      } else if (refreshPeopleLists) refreshPeopleLists();
    },
    [
      activeLeadType,
      activeSubTab,
      dispatch,
      setLeadsData,
      handleRouteChange,
      setSavedFilters,
      handleDefaultValues,
      location.search,
      history,
      getAllFiltersExceptPagination,
      refreshCompanyLists,
      refreshPeopleLists,
      bulkPeopleSearch.isBulkModeActive,
      tabSpecificBulkModeRef,
    ],
  );

  // Handle subtab change (total/new/saved)
  const handleSubTabChange = useCallback(
    (mappedSubTab: LeadFinderSecondaryTab) => {
      if (mappedSubTab === activeSubTab) {
        return;
      }

      const isMovingToSaved = mappedSubTab === LeadFinderSecondaryTab.SAVED;
      const isMovingFromSaved = activeSubTab === LeadFinderSecondaryTab.SAVED;
      const isPersistable =
        (activeSubTab === LeadFinderSecondaryTab.TOTAL &&
          mappedSubTab === LeadFinderSecondaryTab.NEW) ||
        (activeSubTab === LeadFinderSecondaryTab.NEW &&
          mappedSubTab === LeadFinderSecondaryTab.TOTAL);

      const isInBulkMode =
        skipUrlPushForBulkSearchRef.current ||
        (activeLeadType === LeadFinderPrimaryTab.PEOPLE &&
          bulkPeopleSearch.isBulkModeActive);

      dispatch(setActiveSubTab(mappedSubTab));
      setLeadsData({
        profiles: [],
        pagination: null,
      });
      dispatch(clearPeopleSelection());
      dispatch(clearCompanySelection());
      if (refreshPeopleLists) refreshPeopleLists();
      if (refreshCompanyLists) refreshCompanyLists();

      let newUrl = '';

      if (isMovingToSaved || isMovingFromSaved) {
        if (isMovingToSaved) {
          // Store current total/new filters before switching to saved tab (unless bulk search mode)
          if (!isInBulkMode) {
            preservedFiltersRef.current.totalNew = location.search || '';
          }
        } else if (isMovingFromSaved) {
          // Store current saved tab filters before switching away (unless bulk search mode)
          if (!isInBulkMode) {
            preservedFiltersRef.current.saved = location.search || '';
          }
        }
        if (handleRouteChange) handleRouteChange();
        if (setSavedFilters && handleDefaultValues) {
          setSavedFilters(handleDefaultValues(true));
        }

        if (isInBulkMode) {
          if (
            isMovingFromSaved &&
            activeLeadType === LeadFinderPrimaryTab.PEOPLE &&
            tabSpecificFiltersRef.current.people
          ) {
            dispatch(
              setBulkPeopleSearch({
                filters: tabSpecificFiltersRef.current.people,
              }),
            );
            if (skipNextCountCallRef) {
              skipNextCountCallRef.current = true;
            }
          }
          newUrl = `/v2/leads/${activeLeadType}/${mappedSubTab}`;
        } else if (isMovingFromSaved) {
          // When moving FROM saved to total/new, restore preserved total/new filters if they exist
          const preservedSearch = preservedFiltersRef.current.totalNew || '';
          newUrl = `/v2/leads/${activeLeadType}/${mappedSubTab}${preservedSearch}`;
        } else {
          // When moving to saved tab, restore preserved saved tab filters if they exist
          const preservedSearch = preservedFiltersRef.current.saved || '';
          newUrl = `/v2/leads/${activeLeadType}/${mappedSubTab}${preservedSearch}`;
        }
      } else if (isPersistable) {
        if (isInBulkMode) {
          if (
            activeLeadType === LeadFinderPrimaryTab.PEOPLE &&
            tabSpecificFiltersRef.current.people
          ) {
            dispatch(
              setBulkPeopleSearch({
                filters: tabSpecificFiltersRef.current.people,
              }),
            );
          }
          newUrl = `/v2/leads/${activeLeadType}/${mappedSubTab}`;
        } else {
          const currentSearch = location.search || '';
          newUrl = `/v2/leads/${activeLeadType}/${mappedSubTab}${currentSearch}`;
        }
      } else {
        // Default: clear filters
        if (handleRouteChange) handleRouteChange();
        if (setSavedFilters && handleDefaultValues) {
          setSavedFilters(handleDefaultValues(true));
        }
        newUrl = `/v2/leads/${activeLeadType}/${mappedSubTab}`;
      }

      // Update URL
      history.push(newUrl);
    },
    [
      activeSubTab,
      activeLeadType,
      bulkPeopleSearch.isBulkModeActive,
      dispatch,
      setLeadsData,
      handleRouteChange,
      setSavedFilters,
      handleDefaultValues,
      location.search,
      history,
      refreshPeopleLists,
      refreshCompanyLists,
      skipNextCountCallRef,
    ],
  );

  return {
    preservedFiltersRef,
    tabSpecificFiltersRef,
    skipUrlPushForBulkSearchRef,
    handleLeadTypeChange,
    handleSubTabChange,
    handleClearAllPeople,
    handleClearAllCompany,
    getAllFiltersExceptPagination,
  };
};
